import { getHooks } from '@mrdotb/live-react';
import { DOM as FluxonDOM, Hooks as FluxonHooks } from 'fluxon';
import { Socket } from 'phoenix';
import 'phoenix_html';
import { LiveSocket } from 'phoenix_live_view';
import React from 'react';
import { flushSync } from 'react-dom';
import topbar from 'topbar';

import * as Appsignal from './appsignal';
import components from './components/react';
import AssistantPanelHooks from './hooks/assistant_panel';
import BlueprintHooks from './hooks/blueprint';
import BlurFocusHooks from './hooks/blur_focus';
import CalendarDragHooks from './hooks/calendar_drag';
import CalendarPopoverHooks from './hooks/calendar_popover';
import CalendarScrollHooks from './hooks/calendar_scroll';
import ChartHooks from './hooks/chart';
import ChatActionFocusHooks from './hooks/chat_action_focus';
import ChatComposerHooks from './hooks/chat_composer';
import ChatScroll from './hooks/chat_scroll';
import ClearFlashHooks from './hooks/clear_flash';
import ClickHooks from './hooks/click';
import ClickToEditHooks from './hooks/click_to_edit';
import ClipboardHooks from './hooks/clipboard';
import ConfettiHooks from './hooks/confetti';
import { ConfirmDelete } from './hooks/confirm_delete';
import ConversationsScroll from './hooks/conversations_scroll';
import CountdownTimerHooks from './hooks/countdown_timer';
import DatePickerHooks from './hooks/date_picker';
import DecimalInputHooks from './hooks/decimal_input';
import DelayedAnimationHooks from './hooks/delayed_animation';
import DraggableHooks from './hooks/draggable';
import EnterOnFocusHooks from './hooks/enter_on_focus';
import EnterSubmitHooks from './hooks/enter_submit';
import EventPushHooks from './hooks/event_push';
import FileDropZoneHooks from './hooks/file_drop_zone';
import FilterListHooks from './hooks/filter_list';
import FirstImpressionHooks from './hooks/first_impression';
import FlowMapHooks from './hooks/flow_map';
import HiddenFieldHooks from './hooks/hidden_field';
import HoverableHooks from './hooks/hoverable';
import InputEnterHooks from './hooks/input_enter';
import LiveSuperSelect from './hooks/live_super_select';
import MarketingHooks from './hooks/marketing';
import MentionsSelectHooks from './hooks/mentions_select';
import MuxPlayerHooks from './hooks/mux_player';
import NangoHooks from './hooks/nango';
import NavigableSearchHooks from './hooks/navigable_search';
import OnBlurHooks from './hooks/on_blur';
import PopoverHooks from './hooks/popover';
import Rb2bHooks from './hooks/rb2b';
import RenderMentionHooks from './hooks/render_mention';
import ResizableHooks from './hooks/resizable';
import ResizableColumnHooks from './hooks/resizable_column';
import ResizableFieldHooks from './hooks/resizable_field';
import ResizeHandleHooks from './hooks/resize_handle';
import ResponsiveTabsHooks from './hooks/responsive_tabs';
import ScrollClassHooks from './hooks/scroll_class';
import ScrollToElementHooks from './hooks/scroll_to_element';
import ScrollToFieldHooks from './hooks/scroll_to_field';
import SelectedItemHooks from './hooks/selected_item';
import SortableHooks from './hooks/sortable';
import StickyClassHooks from './hooks/sticky_class';
import StopPropagationHooks from './hooks/stop_propagation';
import ThemeTokensHooks from './hooks/theme_tokens';
import TimeHooks from './hooks/time';
import TypewriterHooks from './hooks/typewriter';
import { initLayout } from './layout';
import './marketing/mobile_menu';
import './marketing/scroll_header';
import AppsignalErrorBoundary from './react/AppsignalErrorBoundary';
import Uploaders from './uploaders';
import { getCsrfToken } from './utils/csrf';

const SOCKET_URL_RE = /\b(?:https?|wss?):\/\/\S+/gi;

const sanitizeSocketUrl = value => {
  if (typeof value !== 'string' || value.trim().length === 0) return undefined;

  try {
    const url = new URL(value);
    return url.pathname || '/';
  } catch (_error) {
    return undefined;
  }
};

const sanitizeSocketMessage = value => {
  if (typeof value !== 'string' || value.trim().length === 0) return undefined;

  const sanitized = value.replace(SOCKET_URL_RE, rawUrl => sanitizeSocketUrl(rawUrl) || '[redacted-url]').trim();
  return sanitized.length > 0 ? sanitized : undefined;
};

const extractSocketMetadata = data => {
  if (data instanceof Error) {
    return {
      error_name: data.name,
      error_message: sanitizeSocketMessage(data.message),
    };
  }

  if (data instanceof CloseEvent) {
    return {
      close_code: data.code,
      close_reason: sanitizeSocketMessage(data.reason),
      was_clean: data.wasClean,
    };
  }

  if (typeof data === 'number' || typeof data === 'boolean') {
    return {
      value: data,
    };
  }

  if (typeof data === 'string') {
    return {
      value: sanitizeSocketMessage(data),
    };
  }

  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return {};
  }

  const metadata = {};
  const scalarKeys = ['code', 'event', 'reason', 'status', 'timeout', 'topic', 'type'];

  scalarKeys.forEach(key => {
    const value = data[key];
    if (typeof value === 'string') {
      const sanitized = sanitizeSocketMessage(value);
      if (sanitized) metadata[key] = sanitized;
      return;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      metadata[key] = value;
    }
  });

  return metadata;
};

const getSocketBreadcrumbAction = (kind, message) => {
  if (kind === 'transport') {
    if (!message) return 'transport-error';
    if (message.includes('connected to')) return 'transport-open';
    if (message.includes('heartbeat timeout')) return 'transport-timeout';
    if (message.includes('falling back')) return 'transport-fallback';
    if (message.includes('established')) return 'transport-fallback-established';
    if (message === 'close') return 'transport-close';
    if (message.includes('error')) return 'transport-error';
    return null;
  }

  if (kind === 'channel') {
    if (!message) return null;
    if (message.startsWith('error ')) return 'channel-error';
    if (message.startsWith('timeout ')) return 'channel-timeout';
    if (message.startsWith('close ')) return 'channel-close';
    return null;
  }

  return null;
};

const createLiveSocketLogger = () => (kind, msg, data) => {
  const normalizedMessage =
    typeof msg === 'string'
      ? sanitizeSocketMessage(msg)
      : msg instanceof Error
        ? sanitizeSocketMessage(msg.message)
        : undefined;
  const action = getSocketBreadcrumbAction(kind, normalizedMessage);

  if (!action) return;

  Appsignal.addBreadcrumb({
    category: 'liveview',
    action,
    ...(normalizedMessage ? { message: normalizedMessage } : {}),
    metadata: {
      kind,
      ...extractSocketMetadata(data),
    },
  });
};

const getLiveReactComponentName = hook => {
  const component = hook?._Component;

  return (
    hook?.el?.getAttribute('data-name') || component?.displayName || component?.name || 'AnonymousLiveReactComponent'
  );
};

const getLiveReactProps = hook => {
  const rawProps = hook.el?.getAttribute('data-props') || '{}';
  if (hook._parsedPropsRaw !== rawProps) {
    try {
      hook._parsedPropsValue = rawProps ? JSON.parse(rawProps) : {};
    } catch (_error) {
      hook._parsedPropsValue = {};
    }
    hook._parsedPropsRaw = rawProps;
  }

  const hookElement = hook.el;
  const hookIsConnected = () => hook.isDestroyed !== true && hookElement.isConnected;
  const targetIsConnected = target => {
    if (target === null || target === undefined) return false;
    if (target instanceof Element) return target.isConnected;
    return true;
  };

  return {
    ...(hook._parsedPropsValue || {}),
    pushEvent: (...args) => {
      if (!hookIsConnected()) return undefined;
      return hook.pushEvent(...args);
    },
    pushEventTo: (target, ...args) => {
      if (!hookIsConnected() || !targetIsConnected(target)) return undefined;
      return hook.pushEventTo(target, ...args);
    },
    handleEvent: hook.handleEvent.bind(hook),
    upload: hook.upload.bind(hook),
    uploadTo: hook.uploadTo.bind(hook),
  };
};

const ReactHooks = getHooks(components);
if (ReactHooks.ReactHook) {
  const originalMounted = ReactHooks.ReactHook.mounted;
  const originalUpdated = ReactHooks.ReactHook.updated;

  ReactHooks.ReactHook.mounted = function () {
    this.isDestroyed = false;
    this._syncFirstSuperTextRender = this.el?.getAttribute('data-name') === 'SuperText';
    const result = originalMounted.call(this);
    this._lastRenderedPropsRaw = this.el?.getAttribute('data-props') || null;
    return result;
  };

  ReactHooks.ReactHook._render = function () {
    const render = () => {
      if (!this._root || !this._Component) return;
      const Component = this._Component;
      const props = getLiveReactProps(this);
      const content = React.createElement(Component, props);

      this._root.render(
        Appsignal.appsignal
          ? React.createElement(
              AppsignalErrorBoundary,
              {
                componentName: getLiveReactComponentName(this),
                resetKey: this.el?.getAttribute('data-props') || 'no-props',
                rootElement: this.el,
                source: 'live-react',
              },
              content,
            )
          : content,
      );
    };

    if (this._syncFirstSuperTextRender) {
      this._syncFirstSuperTextRender = false;
      flushSync(render);
      return;
    }
    render();
  };

  ReactHooks.ReactHook.updated = function () {
    const nextPropsRaw = this.el?.getAttribute('data-props') || null;
    if (this._lastRenderedPropsRaw === nextPropsRaw) {
      return;
    }
    this._lastRenderedPropsRaw = nextPropsRaw;
    return originalUpdated.call(this);
  };

  ReactHooks.ReactHook.destroyed = function () {
    this.isDestroyed = true;
    if (!this._root) return;
    this._root.unmount();
    this._root = null;
  };
}

const Hooks = {
  ...ReactHooks,
  ...CalendarDragHooks,
  ...CalendarPopoverHooks,
  ...CalendarScrollHooks,
  ...MarketingHooks,
  ...BlueprintHooks,
  ...ClearFlashHooks,
  ...ClickHooks,
  ...ClickToEditHooks,
  ...ClipboardHooks,
  ...ConfettiHooks,
  ConfirmDelete,
  ...CountdownTimerHooks,
  ...DatePickerHooks,
  ...DecimalInputHooks,
  ...DelayedAnimationHooks,
  ...BlurFocusHooks,
  ...DraggableHooks,
  ...EnterOnFocusHooks,
  ...EnterSubmitHooks,
  ...EventPushHooks,
  ...FileDropZoneHooks,
  ...FilterListHooks,
  ...FirstImpressionHooks,
  ...HiddenFieldHooks,
  ...HoverableHooks,
  ...InputEnterHooks,
  ...LiveSuperSelect,
  ...MentionsSelectHooks,
  ...MuxPlayerHooks,
  ...NangoHooks,
  ...NavigableSearchHooks,
  ...OnBlurHooks,
  ...ResponsiveTabsHooks,
  ...PopoverHooks,
  ...Rb2bHooks,
  ...RenderMentionHooks,
  ...ResizableColumnHooks,
  ...SelectedItemHooks,
  ...ResizableFieldHooks,
  ...ResizableHooks,
  ...ResizeHandleHooks,
  ...AssistantPanelHooks,
  ...ScrollClassHooks,
  ...ScrollToElementHooks,
  ...ScrollToFieldHooks,
  ...SortableHooks,
  ...StickyClassHooks,
  ...StopPropagationHooks,
  ...ThemeTokensHooks,
  ...TimeHooks,
  ...TypewriterHooks,
  ...FluxonHooks,
  ...ChartHooks,
  ...FlowMapHooks,
  ...ChatActionFocusHooks,
  ...ChatComposerHooks,
  ...ChatScroll,
  ...ConversationsScroll,

  Focus: {
    mounted() {
      this.handleEvent('focus', payload => {
        const { name } = payload;
        const elements = document.getElementsByName(name);

        if (elements.length > 0) {
          const lastElement = elements[elements.length - 1]; // Get the last element

          if (lastElement && typeof lastElement.value === 'string') {
            lastElement.focus(); // Focus on the last element

            const value = lastElement.value;
            lastElement.value = ''; // Temporarily clear the value
            lastElement.value = value; // Set it back to move the cursor to the end
          }
        }
      });
    },
  },
};

const isLiveViewPushTimeout = error => Boolean(error && typeof error === 'object' && error.timeout === true);

const patchLiveViewHookTimeoutRejections = liveSocket => {
  const patchViewPrototype = view => {
    const prototype = Object.getPrototypeOf(view);

    if (!prototype || prototype.__hookTimeoutPatchApplied) return;
    if (typeof prototype.pushHookEvent !== 'function') return;

    prototype.pushHookEvent = function (el, targetCtx, event, payload, onReply) {
      if (!this.isConnected()) {
        if (typeof onReply === 'function') onReply(undefined, null);
        return undefined;
      }

      const [ref, els, opts] = this.putRef([{ el, loading: true, lock: true }], event, 'hook');

      this.pushWithReply(() => [ref, els, opts], 'event', {
        type: 'hook',
        event,
        value: payload,
        cid: this.closestComponentID(targetCtx),
      })
        .then(({ reply }) => onReply(reply, ref))
        .catch(error => {
          if (isLiveViewPushTimeout(error)) {
            Appsignal.addBreadcrumb({
              category: 'liveview',
              action: 'hook-push-timeout',
              ...(event ? { message: event } : {}),
              metadata: {
                component_id: this.closestComponentID(targetCtx) ?? undefined,
              },
            });
            return;
          }

          throw error;
        });

      return ref;
    };

    prototype.__hookTimeoutPatchApplied = true;
  };

  const originalNewRootView = liveSocket.newRootView.bind(liveSocket);

  liveSocket.newRootView = (...args) => {
    const view = originalNewRootView(...args);
    patchViewPrototype(view);
    return view;
  };
};

const csrfToken = getCsrfToken();
const liveSocket = new LiveSocket('/live', Socket, {
  uploaders: Uploaders,
  params: {
    _csrf_token: csrfToken,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  },
  logger: createLiveSocketLogger(),
  hooks: Hooks,
  dom: {
    onBeforeElUpdated(from, to) {
      return FluxonDOM.onBeforeElUpdated(from, to);
    },
  },
});
patchLiveViewHookTimeoutRejections(liveSocket);

// Prevent native form submission for LiveView change-only forms (no phx-submit, no action).
// These forms use phx-change for real-time updates and should never submit natively —
// doing so causes a full page GET/POST reload.
window.addEventListener(
  'submit',
  e => {
    const form = e.target;
    if (!form.hasAttribute('phx-submit') && !form.getAttribute('action')) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  },
  { capture: true },
);

// Generic handler for push_event("*") from LiveView that need to be forwarded to elements
// Phoenix LiveView dispatches push_event as phx:event-name on window
// If event.detail contains an 'id', forward the event to that element
const handleElementEvent = event => {
  if (event.detail?.id) {
    const element = document.querySelector(event.detail.id);
    if (element) {
      // Extract event name by removing 'phx:' prefix
      const eventName = event.type.replace('phx:', '');
      element.dispatchEvent(new CustomEvent(eventName, { bubbles: true }));
    }
  }
};

// Register handler for Fluxon events (can be extended for other event patterns)
['popover:close', 'popover:open', 'dialog:close', 'dialog:open'].forEach(eventName => {
  window.addEventListener(`phx:fluxon:${eventName}`, handleElementEvent);
});

// Show progress bar on live navigation and form submits
topbar.config({ barColors: { 0: '#29d' }, shadowColor: 'rgba(0, 0, 0, .3)' });
window.addEventListener('phx:page-loading-start', _info => topbar.show(700));
window.addEventListener('phx:page-loading-stop', _info => topbar.hide());

// used for clearing the input of the search input of the select component
window.addEventListener('clear_input', event => {
  event.target.value = '';
});

window.addEventListener('phx:scroll_dropdown_down', event => {
  const selector = event.detail?.selector;
  if (!selector) return;

  const optionsList = document.querySelector(selector);
  if (!optionsList) return;

  // Wait for DOM patch to settle before adjusting scroll.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      optionsList.scrollTop = optionsList.scrollHeight;
    });
  });
});

// `trigger_change` is our custom event which will enforce reloading elements
// tagged with `trigger_change` attribute. Its walkaround for triggering `phx-change`
// events on structures built programmatically
window.addEventListener('phx:trigger_change', _ => {
  document.querySelectorAll('[trigger-change]').forEach(el => {
    el.dispatchEvent(new Event('input', { bubbles: true }));
  });
});

const focusInputHandler = event => {
  const detail = event?.detail || {};
  if (!detail?.name) return;
  const debugSlash = window.__debugSlash === true;
  const isReplay = detail.__replay === true;
  const requestedOpenSlash = detail.open_slash === true || detail.openSlash === true;

  const map = (window.__superTextFocusInputMap ||= {});
  const now = Date.now();
  Object.entries(map).forEach(([key, value]) => {
    if (now - (value?.ts || 0) > 5000) delete map[key];
  });
  const previous = map[detail.name] || {};
  map[detail.name] = {
    placement: detail.placement,
    openSlash: isReplay ? previous.openSlash === true || requestedOpenSlash : requestedOpenSlash,
    ts: isReplay ? previous.ts || now : now,
  };

  if (isReplay) {
    return;
  }

  if (debugSlash) {
    console.debug('[slash-ab][app] focusInput', {
      name: detail.name,
      placement: detail.placement,
      replay: isReplay,
    });
  }
};

window.addEventListener('phx:focusInput', focusInputHandler);
document.addEventListener('phx:focusInput', focusInputHandler);

// event for copying passed data to user's clipboard
window.addEventListener('phx:copy-to-clipboard', e => {
  const text = e.detail?.clipboard ?? '';
  navigator.clipboard.writeText(text).then(() => {
    const toast = document.createElement('div');
    toast.className = 'clipboard-toast';
    toast.setAttribute('role', 'alert');
    toast.innerHTML =
      '<div class="flex items-start"><div class="shrink-0"><span class="hero-check-circle-mini h-4 w-4 text-green-600"></span></div><div class="ml-3 w-0 flex-1 pt-0.5"><p class="text-gray-900">Copied!</p></div></div>';
    document.body.appendChild(toast);
    setTimeout(() => toast.classList.add('show'), 10);
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 300);
    }, 2000);
  });
});

initLayout();

// event for opening a URL in a new tab
window.addEventListener('phx:open_url', e => {
  if (e.detail?.url) {
    window.open(e.detail.url, '_blank', 'noopener,noreferrer');
  }
});

if (window.env?.APPSIGNAL_FRONTEND_API_KEY) {
  Appsignal.initialize({
    key: window.env.APPSIGNAL_FRONTEND_API_KEY,
    revision: window.env.APPSIGNAL_FRONTEND_REVISION,
  });
}

// connect if there are any LiveViews on the page
liveSocket.connect();

// expose liveSocket on window for web console debug logs and latency simulation:
// >> liveSocket.enableDebug()
// >> liveSocket.enableLatencySim(1000)  // enabled for duration of browser session
// >> liveSocket.disableLatencySim()
window.liveSocket = liveSocket;
